import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircleIcon, HeartIcon, BookOpenIcon, FlameIcon, HeartHandshakeIcon, UsersIcon, ListIcon, MapPinIcon, UserIcon, LinkIcon, ZapIcon, BookmarkIcon, GlobeIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { TagList, TagViewToggle, type TagDisplayMode } from '../components/Content/TagDisplay';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
// Mock data for content warning tags
const contentWarningTags = [{
  id: 'violence',
  name: 'Violence',
  description: 'Books containing scenes of violence, fighting, or physical conflict.',
  count: 278,
  category: 'Physical'
}, {
  id: 'sexual-content',
  name: 'Sexual Content',
  description: 'Books containing explicit sexual scenes or references.',
  count: 312,
  category: 'Sexual'
}, {
  id: 'abuse',
  name: 'Abuse',
  description: 'Books depicting physical, emotional, or psychological abuse.',
  count: 146,
  category: 'Trauma'
}, {
  id: 'self-harm',
  name: 'Self Harm',
  description: 'Books containing references to or depictions of self-harm.',
  count: 87,
  category: 'Mental Health'
}, {
  id: 'suicide',
  name: 'Suicide',
  description: 'Books containing references to or depictions of suicide or suicidal thoughts.',
  count: 92,
  category: 'Mental Health'
}, {
  id: 'addiction',
  name: 'Addiction',
  description: 'Books depicting substance abuse or other addictive behaviors.',
  count: 124,
  category: 'Mental Health'
}, {
  id: 'death',
  name: 'Death',
  description: 'Books containing deaths of characters or discussions of death.',
  count: 256,
  category: 'Trauma'
}, {
  id: 'gore',
  name: 'Gore',
  description: 'Books containing graphic descriptions of blood, injuries, or bodily harm.',
  count: 143,
  category: 'Physical'
}, {
  id: 'sexual-assault',
  name: 'Sexual Assault',
  description: 'Books containing references to or depictions of sexual assault or rape.',
  count: 112,
  category: 'Sexual'
}, {
  id: 'child-abuse',
  name: 'Child Abuse',
  description: 'Books containing references to or depictions of child abuse.',
  count: 78,
  category: 'Trauma'
}, {
  id: 'eating-disorders',
  name: 'Eating Disorders',
  description: 'Books containing references to or depictions of eating disorders.',
  count: 65,
  category: 'Mental Health'
}, {
  id: 'animal-cruelty',
  name: 'Animal Cruelty',
  description: 'Books containing references to or depictions of animal cruelty or harm.',
  count: 54,
  category: 'Physical'
}, {
  id: 'racism',
  name: 'Racism',
  description: 'Books containing references to or depictions of racism or racial slurs.',
  count: 98,
  category: 'Discrimination'
}, {
  id: 'homophobia',
  name: 'Homophobia',
  description: 'Books containing references to or depictions of homophobia or homophobic slurs.',
  count: 87,
  category: 'Discrimination'
}, {
  id: 'transphobia',
  name: 'Transphobia',
  description: 'Books containing references to or depictions of transphobia or transphobic language.',
  count: 65,
  category: 'Discrimination'
}];
const ContentWarnings: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tagViewMode, setTagViewMode] = useState<TagDisplayMode>('cards');
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  // Helper function to get color based on content warning category
  const getContentWarningColor = (category: string) => {
    if (darkMode) {
      switch (category) {
        case 'Physical':
          return 'bg-red-900/30 text-red-300';
        case 'Sexual':
          return 'bg-pink-900/30 text-pink-300';
        case 'Trauma':
          return 'bg-amber-900/30 text-amber-300';
        case 'Mental Health':
          return 'bg-blue-900/30 text-blue-300';
        case 'Discrimination':
          return 'bg-indigo-900/30 text-indigo-300';
        default:
          return dk.badgeNeutral;
      }
    }
    switch (category) {
      case 'Physical':
        return 'bg-red-100 text-red-900';
      case 'Sexual':
        return 'bg-pink-100 text-pink-900';
      case 'Trauma':
        return 'bg-amber-100 text-amber-900';
      case 'Mental Health':
        return 'bg-blue-100 text-blue-900';
      case 'Discrimination':
        return 'bg-indigo-100 text-indigo-900';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };
  const groupedTags = contentWarningTags.reduce((acc, tag) => {
    const category = tag.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tag);
    return acc;
  }, {} as Record<string, typeof contentWarningTags>);
  const filteredGroupedTags = Object.entries(groupedTags).reduce((acc, [category, tags]) => {
    const filteredTags = tags.filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()) || tag.description.toLowerCase().includes(searchQuery.toLowerCase()));
    if (filteredTags.length > 0) acc[category] = filteredTags;
    return acc;
  }, {} as Record<string, typeof contentWarningTags>);
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    subItems: [{
      label: 'Sexual Orientation & Gender',
      href: '/representation?category=Sexual+Orientation+%26+Gender'
    }, {
      label: 'Race & Ethnicity',
      href: '/representation?category=Race+%26+Ethnicity'
    }, {
      label: 'Neurodiversity & Disability',
      href: '/representation?category=Neurodiversity+%26+Disability'
    }, {
      label: 'Mental Health',
      href: '/representation?category=Mental+Health'
    }]
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    subItems: [{
      label: 'Personal Growth',
      href: '/themes?category=Personal+Growth'
    }, {
      label: 'Destiny',
      href: '/themes?category=Destiny'
    }, {
      label: 'Power',
      href: '/themes?category=Power'
    }, {
      label: 'Morality',
      href: '/themes?category=Morality'
    }, {
      label: 'Relationships',
      href: '/themes?category=Relationships'
    }, {
      label: 'Identity',
      href: '/themes?category=Identity'
    }]
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon,
    isActive: true
  }];
  return <div className={dk.page} data-id="element-9086" data-mp="tbq0fpub68ld">
      <ContentPageHeader title="Content Warnings" icon={getContentPageIcon('contentwarnings')} searchPlaceholder="Search content warnings..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-9087" data-mp="tchkq4iggjwc" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9088" data-mp="fet7niumqh1u">
        <div className="flex gap-8" data-id="element-9089" data-mp="afgfqjowijse">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-9090" data-mp="29qzj8irfqzuo">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={[]} onReset={() => setSearchQuery('')} searchable={false} data-id="element-9091" data-mp="31z0nyofzqe4" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-9092" data-mp="3u8sbzvcy95wd">
            <div className={`mb-6 p-4 rounded-md border ${darkMode ? 'bg-amber-900/20 border-amber-800/30' : 'bg-amber-50 border-amber-200'}`} data-id="element-9093" data-mp="3u9tom49m60g7">
              <div className="flex items-start" data-id="element-9094" data-mp="qsl7xnqmh6n2">
                <AlertCircleIcon className={`h-5 w-5 mt-0.5 mr-2 flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} data-id="element-9095" data-mp="qq8sbh8c78d6" />
                <p className={`text-sm ${darkMode ? 'text-amber-200' : 'text-amber-800'}`} data-id="element-9096" data-mp="gt40b0rt8iog">
                  Content warnings are provided to help readers make informed
                  choices and are not judgments on the quality or value of the
                  books.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 mb-4" data-id="element-9097" data-mp="1enwxfvmfd8vi">
              <TagViewToggle mode={tagViewMode} setMode={setTagViewMode} data-id="element-9098" data-mp="1eq142lbpklzh" />
            </div>
            {Object.keys(filteredGroupedTags).length === 0 ? <div className="text-center py-12" data-id="element-9099" data-mp="1on5p2httibep">
                <p className={dk.textMuted} data-id="element-9100" data-mp="10w9sz8ibwu1n">
                  No content warnings found matching your search.
                </p>
              </div> : <div className="space-y-3" data-id="element-9101" data-mp="vz035rr12gda">
                {sortEntriesAlphabetically(Object.entries(filteredGroupedTags)).map(([category, tags]) => <ContentAccordionSection key={category} title={category} itemCount={tags.length} data-id="element-9102" data-mp="14wach7kabvfk">
                    <TagList mode={tagViewMode} colorClassFor={t => getContentWarningColor(t.category || '')} tags={tags.map(tag => ({
                id: tag.id,
                name: tag.name,
                description: tag.description,
                count: tag.count,
                category: tag.category,
                href: `/tagged-books/${tag.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`
              }))} data-id="element-9103" data-mp="d5mda126904j" />
                  </ContentAccordionSection>)}
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default ContentWarnings;